import React from "react";
import { ArrowUpRightIcon } from "lucide-react";
const themes = [{
  number: "01",
  title: "Safety, care and respect",
  description: "Big Yellow’s workplace commitments say all its people deserve to be kept safe and treated with care, dignity and respect."
}, {
  number: "02",
  title: "Leaders on the ground",
  description: "Mine managers, mining engineers, geologists, surveyors and metallurgists work alongside the fleet, so crews work next to the people making the technical calls."
}, {
  number: "03",
  title: "People and capability",
  description: "People & Capability Manager Jonno Seen leads workforce planning, talent development and employee engagement, with a focus on leadership development, diversity and continuous improvement."
}, {
  number: "04",
  title: "Operator training and reliable plant",
  description: "Plant Manager Chris Overton’s focus includes preventative maintenance and operator training, keeping equipment safe, reliable and productive in the field."
}];
const leaders = [{
  name: "Mitchell Wallace",
  role: "Chief Executive Officer",
  years: "30+",
  detail: "years in the resources sector"
}, {
  name: "Matt Miller",
  role: "Chief Operations Officer",
  years: "25+",
  detail: "years in mining and civil contracting"
}, {
  name: "Bradley Gordon",
  role: "Executive Director",
  years: "35+",
  detail: "years across gold, copper and mineral sands"
}, {
  name: "David Edwards",
  role: "Executive Chairman",
  years: "40",
  detail: "years in mining and construction management"
}];
export function LifeAtBigYellow() {
  return <section id="life" aria-labelledby="life-heading" className="bg-black text-white" data-id="element-280" data-mp="3hhtkder0chrn">
      <div className="mx-auto w-full max-w-[1600px] px-6 py-[3.75rem] md:py-[5.25rem] lg:px-12 xl:px-20" data-id="element-281" data-mp="3mh6i4ujf3990">
        <div className="grid gap-14 md:grid-cols-12" data-id="element-282" data-mp="263g9dahcrwt1">
          <div className="md:col-span-5" data-id="element-283" data-mp="2662hpcc2igcb">
            <p className="mb-5 text-[0.8125rem] font-semibold uppercase tracking-[0.14em] text-[#D58D33]" data-id="element-284" data-mp="2b32mljx6ld1u">Life at Big Yellow</p>
            <h2 id="life-heading" className="max-w-xl text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-5xl lg:text-7xl" data-id="element-285" data-mp="1r06kp1zj7cve">The work matters. So do the people doing it.</h2>
            <p className="mt-7 max-w-md text-lg leading-8 text-white/80" data-id="element-286" data-mp="1i7cqzzjc8ay0">A career is more than the equipment you operate. It&apos;s the people beside you, the pride you take in the work and the way you&apos;re treated.</p>
            <blockquote className="mt-10 max-w-md border-l-4 border-[#FFCF53] pl-5" data-id="element-287" data-mp="18bi0ki8vhbko">
              <p className="text-xl font-medium leading-8" data-id="element-288" data-mp="13ac3k886j25c">“We encourage equal opportunity and pride ourselves on our company culture.”</p>
              <footer className="mt-3 text-sm text-white/60" data-id="element-289" data-mp="zbd2wzthdrjp">Big Yellow careers statement</footer>
            </blockquote>
          </div>

          <div className="md:col-span-6 md:col-start-7 md:pt-5" data-id="element-290" data-mp="2tnt59pj5olja">
            {themes.map(theme => <article key={theme.number} className="grid gap-4 border-t border-white/20 py-7 sm:grid-cols-[56px_1fr]" data-id="element-291" data-mp="2opi03s3t2z7e">
                <p className="text-sm font-semibold tracking-[0.12em] text-[#FFCF53]" data-id="element-292" data-mp="2w7g2lcnkla14">{theme.number}</p>
                <div data-id="element-293" data-mp="3157w2b3v547f">
                  <h3 className="text-2xl font-semibold" data-id="element-294" data-mp="314psetevfnph">{theme.title}</h3>
                  <p className="mt-3 max-w-2xl text-base leading-7 text-white/75" data-id="element-295" data-mp="35ykj4gsw92y2">{theme.description}</p>
                </div>
              </article>)}
          </div>
        </div>

        <figure className="mt-16" data-id="element-296" data-mp="2daoempx326qq">
          <div className="relative aspect-[4/3] overflow-hidden sm:aspect-[16/10]" data-id="element-297" data-mp="2i86ociixcpzs">
            <img src="https://cdn.magicpatterns.com/uploads/ouZpU5tEUkh1gFox2HGTu6/1696-5e25075b-7022-4568-bd4a-551a8632f87a-KayleneCAREY_1787428925606.jpg" alt="A Big Yellow crew member in hi-vis and hard hat standing in front of a Big Yellow grader on red earth" width={1440} height={1080} loading="lazy" className="h-full w-full object-cover object-top" data-id="element-298" data-mp="2iaas90gtpp02" />
          </div>
          <figcaption className="mt-3 text-sm text-white/65" data-id="element-299" data-mp="529ukxpsav1q">On site with the Big Yellow fleet.</figcaption>
        </figure>

        <div className="mt-20 border-t border-white/20 pt-10" data-id="element-300" data-mp="4i2a2l896x0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-301" data-mp="f0pmh2wmy8ca">
            <h3 className="text-2xl font-semibold sm:text-3xl" data-id="element-302" data-mp="9zjp2rs15wwf">Led by people who&apos;ve done the work.</h3>
            <a href="https://bigyellow.au/about-us/" className="link-arrow self-start text-white sm:self-auto" data-id="element-303" data-mp="nz1ijkg9tyc4">
              Meet the full leadership team <ArrowUpRightIcon aria-hidden="true" size={17} className="btn-icon" data-id="element-304" data-mp="izf3r6ksoa17" />
            </a>
          </div>
          <ul className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-305" data-mp="srcignb0hk4o">
            {leaders.map(leader => <li key={leader.name} className="flex flex-col" data-id="element-306" data-mp="16qc3zm389iwm">
                <p className="font-display text-5xl font-bold leading-none text-[#FFCF53]" data-id="element-307" data-mp="16ohjg8vvbst2">{leader.years}</p>
                <p className="mt-2 text-sm leading-6 text-white/65" data-id="element-308" data-mp="1ldj8pa24dkn2">{leader.detail}</p>
                <p className="mt-5 text-lg font-semibold" data-id="element-309" data-mp="1lbq2fvvf52fl">{leader.name}</p>
                <p className="text-base text-white/75" data-id="element-310" data-mp="1qc42aqmv962j">{leader.role}</p>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}